import { clsx } from 'clsx'
import FormField from 'components/FormField'
import { observer } from 'mobx-react'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

const RemarkInfo = ({ disabled }: { disabled?: boolean }) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const { control } = useFormContext()
  const isEditMode = !!id
  const isToggleDisabled = !!disabled || isEditMode
  const isTextFieldDisabled = !!disabled
  const remarksRequired = useWatch({
    control,
    name: 'remarksRequired',
  })
  const remarksLabel = useWatch({
    control,
    name: 'remarksLabel',
  })
  const remarksPlaceholder = useWatch({
    control,
    name: 'remarksPlaceholder',
  })
  return (
    <div className="field mt-4">
      <h2 className="text-xl mb-2">{t('Remarks Field Settings')}</h2>
      <p className="text-color-secondary mt-0 mb-3 text-sm">
        {t(
          'This is the setting for the remarks field displayed on the application form (the field that applicants fill in when applying).'
        )}
      </p>

      <FormField
        control={control}
        name="remarksRequired"
        className="mb-1"
        label={t('Required / Optional')}
        labelClassName="font-semibold mb-2 block"
        render={({ field }) => (
          <div className="inline-flex border-1 surface-border border-round overflow-hidden">
            <div
              role="button"
              tabIndex={isToggleDisabled ? -1 : 0}
              onClick={() => !isToggleDisabled && field.onChange(false)}
              onKeyDown={(e) => {
                if (!isToggleDisabled && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault()
                  field.onChange(false)
                }
              }}
              className={clsx(
                'px-4 py-2 cursor-pointer select-none border-right-1 surface-border transition-colors transition-duration-200',
                field.value ? 'bg-transparent ' : 'bg-primary text-white',
                isToggleDisabled ? 'opacity-60 cursor-not-allowed' : ''
              )}
            >
              {t('Optional')}
            </div>
            <div
              role="button"
              tabIndex={isToggleDisabled ? -1 : 0}
              onClick={() => !isToggleDisabled && field.onChange(true)}
              onKeyDown={(e) => {
                if (!isToggleDisabled && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault()
                  field.onChange(true)
                }
              }}
              className={clsx(
                'px-4 py-2 cursor-pointer select-none transition-colors transition-duration-200',
                !field.value ? 'bg-transparent' : 'bg-primary text-white',
                isToggleDisabled ? 'opacity-60 cursor-not-allowed' : ''
              )}
            >
              {t('Required')}
            </div>
          </div>
        )}
      />
      <div className="text-sm text-color-secondary mb-3">
        {t(
          'If set to "Required", this field cannot be left blank when submitting the application form. Once set, this attribute cannot be changed.'
        )}
      </div>

      <FormField
        control={control}
        name="remarksLabel"
        rules={{
          maxLength: {
            value: 50,
            message: t('Please input < 50 charators'),
          },
        }}
        render={({ field, fieldState }) => {
          return (
            <InputText
              {...field}
              value={field.value ?? ''}
              autoComplete="off"
              maxLength={50}
              placeholder={t('Remarks')}
              disabled={isTextFieldDisabled}
              className={clsx({
                'p-invalid': fieldState.invalid,
              })}
            />
          )
        }}
        label={
          <span>
            {t('Display Label')}
            <span className="text-color-secondary font-normal ml-1">
              {t('(If left blank, "Remarks" will be shown)')}
            </span>
          </span>
        }
      />

      <FormField
        control={control}
        name="remarksPlaceholder"
        rules={{
          maxLength: {
            value: 100,
            message: t('Please input < 100 charators'),
          },
        }}
        render={({ field, fieldState }) => {
          return (
            <InputText
              {...field}
              value={field.value ?? ''}
              autoComplete="off"
              maxLength={100}
              placeholder={t('Please enter your remarks')}
              disabled={isTextFieldDisabled}
              className={clsx({
                'p-invalid': fieldState.invalid,
              })}
            />
          )
        }}
        label={
          <span>
            {t('Prompt')}
            <span className="text-color-secondary font-normal ml-1">
              {t('(If left blank, "Please enter your remarks" will be shown)')}
            </span>
          </span>
        }
      />

      <div className="field mt-3 p-3 border-1 border-round surface-border bg-gray-50 ">
        <label className={clsx('font-semibold mb-2 block')}>
          {t('Preview')}
        </label>
        <div className="">
          <label className="font-semibold mb-2 block">
            {remarksLabel?.trim?.() || t('Remarks')}
            {remarksRequired ? <span className="text-red-500">*</span> : null}
          </label>
          <InputTextarea
            value=""
            placeholder={
              remarksPlaceholder?.trim?.() || t('Please enter your remarks')
            }
            readOnly
            rows={3}
            className="w-full bg-surface-50"
          />
        </div>
      </div>
    </div>
  )
}

export default observer(RemarkInfo)
